import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ISLANDS } from "@/lib/island";

const islandLabel = (value) => ISLANDS[value]?.label || "Île non précisée";

const STATUSES = [
  { value: "new", label: "Nouvelles", singular: "Nouvelle demande" },
  { value: "researching", label: "Recherche fournisseur", singular: "Recherche fournisseur" },
  { value: "quoted", label: "Devis préparés", singular: "Devis préparé" },
  { value: "sent", label: "Devis envoyés", singular: "Devis envoyé" },
  { value: "closed", label: "Clôturées", singular: "Clôturée" },
];

function formatDate(value) {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "Date non renseignée";
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium", timeStyle: "short", timeZone: "Indian/Reunion",
  }).format(date);
}

function errorMessage(error, fallback) {
  if (error?.response?.status === 401) return "Votre session a expiré. Reconnectez-vous pour continuer.";
  if (error?.response?.status === 403) return "Cet espace est réservé aux administrateurs.";
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

export default function LeadRequests() {
  const { user, setAuthOpen, logout } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [islandFilter, setIslandFilter] = useState("all");
  const [saving, setSaving] = useState({});
  const [rowErrors, setRowErrors] = useState({});

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/callback-requests");
      if (!Array.isArray(data)) throw new Error("Unexpected response");
      setRequests(data);
    } catch (err) {
      setError(errorMessage(err, "Impossible de charger les demandes. Réessayez."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") loadRequests();
    else setRequests([]);
  }, [user, loadRequests]);

  const updateStatus = async (request, status) => {
    if (saving[request.id] || request.status === status) return;
    setSaving((current) => ({ ...current, [request.id]: true }));
    setRowErrors((current) => ({ ...current, [request.id]: "" }));
    setNotice("");
    try {
      const { data } = await api.patch(`/admin/callback-requests/${encodeURIComponent(request.id)}`, { status });
      setRequests((current) => current.map((item) => item.id === request.id ? { ...item, ...data } : item));
      const label = STATUSES.find((item) => item.value === status)?.singular;
      setNotice(`Demande de ${request.name || "ce client"} : ${label}. Statut enregistré.`);
    } catch (err) {
      setRowErrors((current) => ({
        ...current,
        [request.id]: errorMessage(err, "Le statut n’a pas été enregistré. Réessayez."),
      }));
    } finally {
      setSaving((current) => ({ ...current, [request.id]: false }));
    }
  };

  if (user === null) return <main id="main-content" className="max-w-5xl mx-auto px-5 py-16" role="status">Vérification de votre accès…</main>;

  if (user?.role !== "admin") {
    return (
      <main id="main-content" className="max-w-xl mx-auto px-5 py-16 text-center">
        <h1 className="text-3xl font-bold text-slate-950">Suivi des demandes</h1>
        <p className="mt-4 text-slate-600">Connectez-vous avec un compte administrateur pour consulter et suivre les demandes clients.</p>
        <button type="button" onClick={() => setAuthOpen(true)} className="btn-primary mt-6">Se connecter</button>
        <Link to="/" className="block mt-5 text-slate-700 underline">Retour au site</Link>
      </main>
    );
  }

  const query = search.trim().toLocaleLowerCase("fr");
  const visible = requests.filter((request) => (
    (filter === "all" || request.status === filter)
    && (islandFilter === "all" || (islandFilter === "none" ? !ISLANDS[request.island] : request.island === islandFilter))
    && (!query || [request.name, request.phone, request.email, request.designation, request.reference, request.message, request.id]
      .some((value) => String(value || "").toLocaleLowerCase("fr").includes(query)))
  ));
  const anySaving = Object.values(saving).some(Boolean);

  return (
    <main id="main-content" className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6" data-testid="lead-requests-page">
      <div className="max-w-6xl mx-auto">
        <header className="flex flex-wrap items-start justify-between gap-5 mb-7">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-red-700">Easy Pièces · Administration</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-950 mt-2">Suivi des demandes</h1>
            <p className="text-slate-600 mt-3">Demande → recherche fournisseur → préparation du devis → envoi au client.</p>
            <p className="text-sm text-slate-500 mt-2">Les statuts servent au suivi interne. Les devis sont préparés et envoyés par votre équipe.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={logout} className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold">Déconnexion</button>
            <Link to="/admin" className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold">Administration</Link>
            <Link to="/admin?tab=suppliers" className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold">Recherche fournisseurs</Link>
            <button type="button" disabled={loading || anySaving} onClick={loadRequests} className="rounded-xl bg-slate-950 text-white px-4 py-3 text-sm font-semibold disabled:opacity-50">
              {loading ? "Chargement…" : "Actualiser"}
            </button>
          </div>
        </header>

        {error && <div role="alert" className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">{error} <button type="button" onClick={() => setAuthOpen(true)} className="underline font-semibold ml-2">Connexion</button></div>}
        <p role="status" className="text-sm text-emerald-800 mb-4">{notice}</p>

        <nav aria-label="Filtrer les demandes par statut" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
          {[{ value: "all", label: "Toutes" }, ...STATUSES].map((status) => (
            <button key={status.value} type="button" aria-pressed={filter === status.value} onClick={() => setFilter(status.value)}
              className={`rounded-2xl border p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 ${filter === status.value ? "bg-slate-950 border-slate-950 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-500"}`}>
              <span className="text-sm block">{status.label}</span>
              <span className="text-2xl font-bold block mt-2">{status.value === "all" ? requests.length : requests.filter((item) => item.status === status.value).length}</span>
            </button>
          ))}
        </nav>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <label className="block w-full sm:max-w-md text-sm font-semibold text-slate-700">Rechercher une demande
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nom, téléphone, pièce, référence…" className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 font-normal focus:outline-none focus:ring-2 focus:ring-red-600" />
          </label>
          <label className="block text-sm font-semibold text-slate-700">Île
            <select value={islandFilter} onChange={(event) => setIslandFilter(event.target.value)} className="mt-2 block rounded-xl border border-slate-300 bg-white px-4 py-3 font-normal">
              <option value="all">Toutes les îles</option>
              {Object.entries(ISLANDS).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}
              <option value="none">Île non précisée</option>
            </select>
          </label>
          <p className="text-sm text-slate-500" role="status">{visible.length} demande{visible.length !== 1 ? "s" : ""} affichée{visible.length !== 1 ? "s" : ""} · 500 dernières maximum</p>
        </div>

        <section aria-label="Demandes clients" aria-busy={loading} className="space-y-4">
          {loading && requests.length === 0 ? <p role="status" className="p-8 bg-white rounded-2xl border border-slate-200">Chargement des demandes…</p> : null}
          {!loading && !error && visible.length === 0 ? <p className="p-8 bg-white rounded-2xl border border-slate-200 text-slate-600">{requests.length === 0 ? "Aucune demande reçue pour le moment." : "Aucune demande ne correspond à ces filtres."}</p> : null}
          {visible.map((request) => {
            const tel = String(request.phone || "").replace(/[^+\d]/g, "");
            const statusKnown = STATUSES.some((status) => status.value === request.status);
            return (
              <article key={request.id} className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm" aria-labelledby={`lead-${request.id}`}>
                <div className="flex flex-wrap justify-between gap-5">
                  <div className="min-w-0">
                    <h2 id={`lead-${request.id}`} className="text-xl font-bold text-slate-950 break-words">{request.name || "Client sans nom"}</h2>
                    <span className={`inline-block mt-2 rounded-full px-3 py-1 text-xs font-bold ${ISLANDS[request.island] ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-600"}`}>{islandLabel(request.island)}</span>
                    <p className="text-xs text-slate-500 mt-2 break-all">Demande {request.id} · {formatDate(request.created_at)} (heure Réunion)</p>
                    <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm">
                      {tel && <a className="font-semibold text-slate-800 underline break-all" href={`tel:${tel}`}>{request.phone}</a>}
                      {request.email && <a className="font-semibold text-slate-800 underline break-all" href={`mailto:${encodeURIComponent(request.email)}`}>{request.email}</a>}
                    </div>
                  </div>
                  <label className="block text-xs font-semibold text-slate-600 w-full sm:w-60">Étape de traitement
                    <select value={request.status || ""} disabled={Boolean(saving[request.id]) || loading} onChange={(event) => updateStatus(request, event.target.value)} className="block w-full mt-2 rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-950 disabled:opacity-50" aria-label={`Statut de la demande de ${request.name || request.id}`}>
                      {!statusKnown && <option value={request.status || ""} disabled>{request.status || "Non renseigné"}</option>}
                      {STATUSES.map((status) => <option key={status.value} value={status.value}>{status.singular}</option>)}
                    </select>
                    {saving[request.id] && <span className="block mt-2" role="status">Enregistrement…</span>}
                  </label>
                </div>
                {(request.designation || request.reference) && <p className="mt-5 text-sm text-slate-700"><strong>{request.designation || "Pièce demandée"}</strong>{request.reference ? ` · Réf. ${request.reference}` : ""}</p>}
                <p className="mt-4 rounded-xl bg-slate-50 p-4 text-sm leading-relaxed text-slate-700 whitespace-pre-wrap break-words">{request.message || "Aucune précision transmise."}</p>
                {rowErrors[request.id] && <p role="alert" className="mt-3 text-sm font-medium text-red-700">{rowErrors[request.id]}</p>}
              </article>
            );
          })}
        </section>
      </div>
    </main>
  );
}
